import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { api } from '../utils/api';
import { formatCurrency } from '../utils/currency';

interface PIXPaymentData {
  id: string;
  qrCode?: string;
  qrCodeBase64?: string;
  pixCopyPaste?: string;
  amount: number;
}

export const AddCreditPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, refreshUser } = useAuth();
  const { socket, isConnected } = useWebSocket();
  const [creditAmount, setCreditAmount] = useState('');
  const [addingCredit, setAddingCredit] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [pixPaymentData, setPixPaymentData] = useState<PIXPaymentData | null>(null);
  const [showPixPage, setShowPixPage] = useState(false);
  const [waitingForPayment, setWaitingForPayment] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  // Get the return URL from navigation state (if coming from machine activation page)
  const returnUrl = (location.state as any)?.returnUrl || '/';

  const handleAddCredit = async () => {
    const amount = parseFloat(creditAmount);
    if (!amount || amount <= 0) {
      setMessage({ type: 'error', text: 'Por favor, insira um valor válido' });
      return;
    }

    try {
      setAddingCredit(true);
      setMessage(null);
      
      // Create PIX payment for credit
      const response = await api.post('/payments/pix', {
        amount,
        description: `Crédito de conta - ${formatCurrency(amount)}`,
      });

      console.log('PIX Payment Response:', response.data);

      const pixData = response.data.data.pixPayment;
      setPixPaymentData({
        id: pixData.id,
        qrCode: pixData.qrCode,
        qrCodeBase64: pixData.qrCodeBase64,
        pixCopyPaste: pixData.pixCopyPaste,
        amount: amount
      });
      setShowPixPage(true);

    } catch (error: any) {
      console.error('Failed to add credit:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || error.response?.data?.message || 'Falha ao adicionar crédito' });
    } finally {
      setAddingCredit(false);
    }
  };

  const handleCopyPixCode = () => {
    if (pixPaymentData?.pixCopyPaste) {
      navigator.clipboard.writeText(pixPaymentData.pixCopyPaste);
      setMessage({ type: 'success', text: 'Código PIX copiado!' });
    }
  };

  const handleBackFromPixPage = () => {
    setShowPixPage(false);
    setPixPaymentData(null);
    setCreditAmount('');
    setWaitingForPayment(false);
  };

  const handleCheckPaymentStatus = async () => {
    if (!pixPaymentData || checkingPayment) return;

    try {
      setCheckingPayment(true);
      setMessage(null);

      // Actually verify the payment with the backend instead of assuming it was
      // paid. Only show success once Mercado Pago reports it approved.
      const res = await api.get(`/payments/status/${pixPaymentData.id}`);
      const status = res.data?.data?.status;

      if (status === 'approved') {
        // Ask the backend to credit it now (idempotent — harmless if the
        // webhook already did), so the refreshed balance includes this payment.
        // Don't swallow a failure: confirm answers 200 when the webhook already
        // credited it, so an error here means the credit really did not land.
        await api.post(`/payments/confirm/${pixPaymentData.id}`);
        setWaitingForPayment(false);
        await refreshUser();
        setShowSuccessModal(true);
        setTimeout(() => {
          navigate(returnUrl);
        }, 2000);
      } else if (status === 'rejected' || status === 'cancelled') {
        setWaitingForPayment(false);
        setMessage({
          type: 'error',
          text: 'Pagamento não foi aprovado. Por favor, tente novamente.'
        });
      } else {
        setMessage({
          type: 'error',
          text: 'Ainda não identificamos o pagamento. Se você já pagou, aguarde alguns segundos e tente novamente.'
        });
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setMessage({
        type: 'error',
        text: 'Erro ao verificar o pagamento. Por favor, tente novamente.'
      });
    } finally {
      setCheckingPayment(false);
    }
  };

  // WebSocket listener for payment confirmation
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handlePaymentConfirmed = async (data: any) => {
      console.log('Payment confirmed via WebSocket:', data);
      
      setWaitingForPayment(false);
      setMessage({ 
        type: 'success', 
        text: 'Pagamento confirmado! Créditos adicionados à sua conta.' 
      });
      
      // Refresh user data to show new balance
      await refreshUser();
      
      // Redirect back to the page user came from (or homepage)
      setTimeout(() => {
        navigate(returnUrl);
      }, 2000);
    };

    const handlePaymentFailed = (data: any) => {
      console.log('Payment failed via WebSocket:', data);
      
      setWaitingForPayment(false);
      setMessage({ 
        type: 'error', 
        text: 'Pagamento não foi aprovado. Por favor, tente novamente.' 
      });
    };

    // Listen for payment events
    socket.on('payment-confirmed', handlePaymentConfirmed);
    socket.on('payment-failed', handlePaymentFailed);

    // Cleanup listeners
    return () => {
      socket.off('payment-confirmed', handlePaymentConfirmed);
      socket.off('payment-failed', handlePaymentFailed);
    };
  }, [socket, isConnected, showPixPage, refreshUser, navigate]);

  // Set waiting state when PIX page opens
  useEffect(() => {
    if (showPixPage && pixPaymentData) {
      setWaitingForPayment(true);
    }
  }, [showPixPage, pixPaymentData]);

  // Polling fallback: the confirmation above is pushed over WebSocket, but if
  // the socket is disconnected when the (possibly retried) webhook confirms the
  // payment, that push is missed and the screen would hang on "waiting". Poll
  // the payment status while waiting so the screen still converges on its own.
  useEffect(() => {
    if (!waitingForPayment || !pixPaymentData) return;

    let cancelled = false;
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/payments/status/${pixPaymentData.id}`);
        const status = res.data?.data?.status;
        if (!cancelled && status === 'approved') {
          // Credit it here too - without this the screen reported success while
          // the balance only moved if the webhook happened to arrive. A failure
          // throws into the catch below and the next tick retries.
          await api.post(`/payments/confirm/${pixPaymentData.id}`);
          setWaitingForPayment(false);
          await refreshUser();
          setShowSuccessModal(true);
          setTimeout(() => navigate(returnUrl), 2000);
        }
      } catch (error) {
        // Transient (e.g. still pending / MP unreachable) — keep polling.
        console.debug('Payment status poll failed, will retry:', error);
      }
    }, 5000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [waitingForPayment, pixPaymentData, refreshUser, navigate, returnUrl]);

  const quickAmounts = [10, 20, 50, 100];

  // Show PIX payment page if PIX payment is active
  if (showPixPage && pixPaymentData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-500 to-orange-400 relative overflow-hidden">
        {/* Animated background circles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute w-96 h-96 bg-white/20 rounded-full -top-48 -left-48 animate-pulse"></div>
          <div className="absolute w-64 h-64 bg-white/10 rounded-full top-1/4 -right-32 animate-bounce" style={{ animationDuration: '3s' }}></div>
        </div>

        {/* Header */}
        <header className="bg-gradient-to-r from-orange-600 to-orange-500 shadow-lg sticky top-0 z-30">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <div className="flex justify-between items-center">
              <button
                onClick={handleBackFromPixPage}
                className="p-2 hover:bg-orange-700 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              
              <h1 className="text-white text-lg sm:text-xl font-bold">Pagamento PIX</h1>
              
              <div className="w-10"></div>
            </div>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-3 sm:px-4 py-4 sm:py-6 relative z-10">
          {/* Message */}
          {message && (
            <div className={`mb-4 p-4 rounded-lg ${
              message.type === 'success' ? 'bg-white text-orange-800 border-2 border-orange-200' : 'bg-white text-red-800 border-2 border-red-200'
            }`}>
              <span className="text-sm font-medium">{message.text}</span>
            </div>
          )}

          {/* Amount Card */}
          <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6 text-center">
            <p className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2">Valor a pagar</p>
            <p className="text-4xl sm:text-5xl font-bold text-orange-600 mb-1 sm:mb-2">{formatCurrency(pixPaymentData.amount)}</p>
            <p className="text-xs sm:text-sm text-gray-500">Você receberá {pixPaymentData.amount.toFixed(0)} minutos de uso</p>
          </div>

          {/* PIX Code Card */}
          <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4 text-center">Código PIX Copia e Cola</h2>
            
            {/* PIX Code Preview */}
            <div className="bg-gray-50 border-2 border-gray-300 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4">
              <p className="text-xs sm:text-sm font-mono text-gray-800 truncate text-center">
                {pixPaymentData.pixCopyPaste}
              </p>
            </div>

            {/* Copy Button */}
            <button
              onClick={handleCopyPixCode}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 sm:py-4 px-4 sm:px-6 rounded-lg transition-colors shadow-lg text-base sm:text-lg flex items-center justify-center"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copiar Código PIX
            </button>
          </div>

          {/* Update Balance Button */}
          <button
            onClick={handleCheckPaymentStatus}
            disabled={checkingPayment}
            className="w-full bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white font-bold py-3 sm:py-4 px-4 sm:px-6 rounded-lg transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed text-base sm:text-lg flex items-center justify-center"
          >
            {checkingPayment ? (
              <>
                <svg className="animate-spin w-5 h-5 sm:w-6 sm:h-6 mr-2" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Verificando...
              </>
            ) : (
              <>
                <svg className="w-5 h-5 sm:w-6 sm:h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Atualizar Saldo
              </>
            )}
          </button>
        </main>

        {/* Success Modal */}
        {showSuccessModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center transform scale-100 animate-in">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
                <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Saldo Atualizado!</h3>
              <p className="text-gray-600 mb-4">Seu saldo foi atualizado com sucesso.</p>
              <div className="flex items-center justify-center text-sm text-gray-500">
                <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Redirecionando...
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-500 to-orange-400 relative overflow-hidden">
      {/* Animated background circles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-96 h-96 bg-white/20 rounded-full -top-48 -left-48 animate-pulse"></div>
        <div className="absolute w-64 h-64 bg-white/10 rounded-full top-1/4 -right-32 animate-bounce" style={{ animationDuration: '3s' }}></div>
        <div className="absolute w-80 h-80 bg-orange-600/10 rounded-full bottom-0 left-1/4 animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Header */}
      <header className="bg-gradient-to-r from-orange-600 to-orange-500 shadow-lg sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-orange-700 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            
            <div className="flex items-center">
              <img 
                src="/assets/upcar-logo-preto.png" 
                alt="UpCar Aspiradores" 
                className="h-12 w-auto"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const parent = e.currentTarget.parentElement;
                  if (parent) {
                    parent.innerHTML = '<div class="text-white text-center"><h1 class="text-xl font-bold">UpCar</h1><p class="text-xs text-orange-100">Aspiradores</p></div>';
                  }
                }}
              />
            </div>
            
            <button
              onClick={logout}
              className="p-2 hover:bg-orange-700 rounded-lg transition-colors"
              title="Sair"
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Current Balance Card */}
        <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-4 mb-4">
          <div className="text-center">
            <p className="text-xs text-gray-600 mb-1">Saldo Atual</p>
            <div className="text-3xl font-bold text-orange-600 mb-1">
              {formatCurrency(user?.accountBalance || 0)}
            </div>
            <p className="text-xs text-gray-500">1 R$ = 1 minuto de uso</p>
          </div>
        </div>

        {/* Add Credit Form */}
        <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-6">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center mr-3">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Adicionar Crédito</h2>
              <p className="text-xs text-gray-600">Recarregue sua conta para usar os aspiradores</p>
            </div>
          </div>

          {/* Message */}
          {message && (
            <div className={`mb-4 p-3 rounded-lg flex justify-between items-center ${
              message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              <span className="text-sm font-medium">{message.text}</span>
              <button
                onClick={() => setMessage(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {/* Quick Amount Buttons */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Valores Rápidos</label>
            <div className="grid grid-cols-4 gap-2">
              {quickAmounts.map((amount) => (
                <button
                  key={amount}
                  onClick={() => setCreditAmount(amount.toString())}
                  className={`py-3 px-2 rounded-lg font-bold text-base transition-all ${
                    creditAmount === amount.toString()
                      ? 'bg-orange-600 text-white shadow-lg scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  R$ {amount}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Amount Input */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Ou insira um valor personalizado</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg font-bold">R$</span>
              <input
                type="number"
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
                placeholder="Digite aqui o valor que deseja adicionar"
                min="1"
                step="0.01"
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 font-bold text-xl text-center"
              />
            </div>
            {creditAmount && parseFloat(creditAmount) > 0 && (
              <p className="text-xs text-gray-500 mt-1 text-center">
                Você receberá {parseFloat(creditAmount).toFixed(0)} minutos de uso
              </p>
            )}
          </div>

          {/* Payment Method Selection */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Método de Pagamento: PIX</label>
          </div>

          {/* Add Credit Button */}
          <button
            onClick={handleAddCredit}
            disabled={addingCredit || !creditAmount || parseFloat(creditAmount) <= 0}
            className="w-full bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white font-bold py-3 px-6 rounded-lg transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed text-base"
          >
            {addingCredit ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processando...
              </span>
            ) : (
              <span className="flex items-center justify-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Pagar com PIX
              </span>
            )}
          </button>

        </div>
      </main>
    </div>
  );
};
